/**
 * Google Map preview for selected locations (Phase E.2).
 *
 * Renders a Google Map below the location chip list. Each selected
 * GeoTargetSuggestion gets geocoded (client-side via Google's
 * Geocoding API) into a lat/lng pin plus a visible region highlight.
 * Map auto-fits to the highlighted areas.
 *
 * Gated by VITE_GOOGLE_MAPS_API_KEY — when missing, component
 * renders an inline "no key configured" placeholder instead of
 * crashing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  useMap,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

import { Badge } from '@/components/ui/badge';
import {
  searchGeoTargets,
  type GeoTargetSuggestion,
} from '@/services/googleAdsCampaignsService';


const API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';

// Google Maps "Map ID" — required by AdvancedMarker, and it has to be one that
// EXISTS in the Cloud console: an unregistered id leaves the pins unrendered
// (developers.google.com/maps/documentation/javascript/advanced-markers/start).
// 'agileco_keyword_research_map' was never registered, so this now falls back
// to Google's published development id and takes a real one from the
// environment when a project has created it.
const DEFAULT_MAP_ID =
  (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined)?.trim() ||
  'DEMO_MAP_ID';

const US_CENTER = { lat: 39.5, lng: -98.35 };

// Google Maps overlay APIs (Pin, Polygon) parse these strings themselves rather
// than handing them to CSS, so `var(--color-coral)` would not resolve — they have
// to stay literal. Keep the values aligned with the theme tokens in
// frontend/src/index.css. To make these follow a runtime theme override they need
// the JS token bridge (`readBrandToken`) added for the antd theme in #960.
// Values mirror the new brand tokens (azure accent + neutral grey); the `coral`/
// `teal` key names are retained for compatibility with call sites.
const MAP_COLORS = {
  coral: '#0A76A8',
  coralDark: '#074E6F',
  teal: '#6B7280',
  tealDark: '#374151',
  regionFill: '#5B8DEF',
  regionStroke: '#4F7FE8',
} as const;

interface GeoBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface GeoPoint {
  lat: number;
  lng: number;
}

// Hand-tuned approximation for the St. Louis DMA used by the Google Ads
// picker smoke test. This is intentionally not labeled as an official
// boundary; it is a visual coverage preview only.
const ST_LOUIS_DMA_APPROX_PATH: GeoPoint[] = [
  { lat: 39.55, lng: -91.35 },
  { lat: 39.28, lng: -90.62 },
  { lat: 39.18, lng: -89.48 },
  { lat: 38.82, lng: -88.74 },
  { lat: 38.34, lng: -88.15 },
  { lat: 37.92, lng: -88.42 },
  { lat: 37.56, lng: -88.92 },
  { lat: 37.24, lng: -89.58 },
  { lat: 36.52, lng: -90.18 },
  { lat: 37.18, lng: -90.82 },
  { lat: 37.18, lng: -91.86 },
  { lat: 37.74, lng: -91.70 },
  { lat: 38.12, lng: -92.18 },
  { lat: 38.54, lng: -91.82 },
];


export interface GeocodedPin {
  /** GeoTarget that produced this pin. */
  location: GeoTargetSuggestion;
  /** Geocoded latitude. */
  lat: number;
  /** Geocoded longitude. */
  lng: number;
  /** Raw bounding box from Geocoding API, when present. */
  geocoderBounds?: GeoBounds;
  /** Bounds we trust enough to render and fit to. */
  highlightBounds: GeoBounds;
  /** Optional regional polygon used when bbox-only would look too artificial. */
  highlightPath?: GeoPoint[];
  /** True when Geocoding returned a bbox too broad/wrong for this target. */
  usedFallbackBounds: boolean;
  /** Short reason shown in hover/tooling/debug text. */
  boundsSource: 'geocoder' | 'local_fallback';
}


interface LocationMapProps {
  tenantId: string;
  selected: GeoTargetSuggestion[];
  onPickLocation?: (loc: GeoTargetSuggestion) => void;
}


export function LocationMap({
  tenantId,
  selected,
  onPickLocation,
}: LocationMapProps) {
  if (!API_KEY) {
    return (
      <div className="rounded border border-dashed border-border bg-warm-bg p-3 text-xs text-muted-foreground">
        Map preview disabled —{' '}
        <code className="rounded bg-card px-1 py-0.5">
          VITE_GOOGLE_MAPS_API_KEY
        </code>{' '}
        not set in <code>frontend/.env.local</code>.
      </div>
    );
  }

  return (
    <APIProvider apiKey={API_KEY}>
      <LocationMapInner
        tenantId={tenantId}
        selected={selected}
        onPickLocation={onPickLocation}
      />
    </APIProvider>
  );
}


function LocationMapInner({
  tenantId,
  selected,
  onPickLocation,
}: {
  tenantId: string;
  selected: GeoTargetSuggestion[];
  onPickLocation?: (loc: GeoTargetSuggestion) => void;
}) {
  const [pins, setPins] = useState<GeocodedPin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clickPoint, setClickPoint] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [clickStatus, setClickStatus] = useState<string | null>(null);
  const [clickError, setClickError] = useState<string | null>(null);

  // Geocode each selected location. Uses an in-memory cache keyed on
  // resource_name so removing+re-adding a chip doesn't re-geocode.
  const cacheRef = useRef<Map<string, GeocodedPin>>(new Map());
  const selectedRef = useRef(selected);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    async function geocodeAll() {
      // Geocoder is loaded via the APIProvider; wait until it's
      // available on the global google.maps namespace.
      const google = (window as unknown as { google?: typeof globalThis.google })
        .google;
      if (!google?.maps?.Geocoder) {
        setLoading(false);
        return;
      }
      const geocoder = new google.maps.Geocoder();
      setLoading(true);
      const out: GeocodedPin[] = [];
      if (selected.length === 0) {
        setPins([]);
        setLoading(false);
        return;
      }
      for (const loc of selected) {
        const cacheKey = locationCacheKey(loc);
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
          out.push(cached);
          continue;
        }
        try {
          // Use the canonical name — it's most likely to geocode
          // correctly ("St. Louis MO, United States" vs just
          // "St. Louis MO" which is ambiguous globally).
          const result = await geocoder.geocode({
            address: loc.canonical_name || loc.name,
          });
          if (cancelled) return;
          const first = result.results?.[0];
          if (!first) continue;
          const location = first.geometry.location;
          const vp = first.geometry.viewport;
          const lat = location.lat();
          const lng = location.lng();
          const geocoderBounds = vp
            ? {
                north: vp.getNorthEast().lat(),
                south: vp.getSouthWest().lat(),
                east: vp.getNorthEast().lng(),
                west: vp.getSouthWest().lng(),
              }
            : undefined;
          const boundsDecision = selectHighlightBounds(loc, lat, lng, geocoderBounds);
          const pin: GeocodedPin = {
            location: loc,
            lat,
            lng,
            geocoderBounds,
            highlightBounds: boundsDecision.bounds,
            highlightPath: boundsDecision.path,
            usedFallbackBounds: boundsDecision.source === 'local_fallback',
            boundsSource: boundsDecision.source,
          };
          cacheRef.current.set(cacheKey, pin);
          out.push(pin);
        } catch (err) {
          // Skip this pin — log but don't crash the whole map
          console.warn('[GEOCODE] failed for', loc.canonical_name, err);
        }
      }
      if (!cancelled) {
        setPins(out);
        setLoading(false);
      }
    }

    geocodeAll().catch((err) => {
      if (!cancelled) {
        setError(String(err?.message ?? err));
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const handleMapClick = useCallback(
    async (event: MapMouseEvent) => {
      const latLng = event.detail.latLng;
      if (!latLng || !tenantId || !onPickLocation) return;

      const google = (window as unknown as { google?: typeof globalThis.google })
        .google;
      if (!google?.maps?.Geocoder) {
        setClickError('Map matching is still loading. Try again in a moment.');
        return;
      }

      setClickPoint(latLng);
      setClickStatus('Matching clicked area...');
      setClickError(null);

      try {
        const geocoder = new google.maps.Geocoder();
        const result = await geocoder.geocode({ location: latLng });
        const first = result.results?.[0];
        if (!first) {
          throw new Error('No geocoded address found for that point.');
        }

        const match = await findGeoTargetForGeocodedResult({
          tenantId,
          result: first,
          selectedResourceNames: new Set(
            selectedRef.current.map((s) => s.resource_name),
          ),
        });

        if (!match) {
          throw new Error("Couldn't match that area to a Google Ads location.");
        }

        onPickLocation(match);
        setClickStatus(`Added ${match.name}`);
        setClickPoint(null);
        window.setTimeout(() => {
          setClickStatus((current) =>
            current === `Added ${match.name}` ? null : current,
          );
        }, 2400);
      } catch (err) {
        setClickError(
          `${String((err as Error)?.message ?? err)} Search by name instead.`,
        );
        setClickStatus(null);
      }
    },
    [onPickLocation, tenantId],
  );

  // Initial center — first pin or fallback to US center.
  const initialCenter = useMemo(
    () => (pins[0] ? { lat: pins[0].lat, lng: pins[0].lng } : US_CENTER),
    [pins],
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 text-teal" />
        Map preview
        {loading && (
          <>
            <Loader2 className="h-3 w-3 animate-spin" />
            geocoding…
          </>
        )}
        {clickStatus && (
          <>
            <Loader2
              className={
                clickPoint ? 'h-3 w-3 animate-spin text-coral' : 'hidden'
              }
            />
            <span className={clickPoint ? 'text-coral-dark' : 'text-teal-dark'}>
              {clickStatus}
            </span>
          </>
        )}
        {error && <span className="text-destructive">— {error}</span>}
        {clickError && <span className="text-destructive">— {clickError}</span>}
        {pins.length > 0 && !loading && (
          <Badge variant="outline" className="ml-auto text-[10px]">
            {pins.length} pin{pins.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>
      <div className="relative overflow-hidden rounded border border-border" style={{ height: 380 }}>
        <GoogleMap
          mapId={DEFAULT_MAP_ID}
          defaultCenter={initialCenter}
          defaultZoom={pins.length === 0 || pins.length > 1 ? 4 : 8}
          gestureHandling="cooperative"
          disableDefaultUI={false}
          onClick={handleMapClick}
          style={{ width: '100%', height: '100%' }}
        >
          <FitToBounds pins={pins} />
          {/* Phase E.3 — semi-transparent overlay per location bbox */}
          <RegionOverlays pins={pins} />
          {clickPoint && (
            <AdvancedMarker position={clickPoint} title="Matching clicked area">
              <Pin
                background={MAP_COLORS.coral}
                glyphColor="white"
                borderColor={MAP_COLORS.coralDark}
              />
            </AdvancedMarker>
          )}
          {pins.map((pin) => (
            <AdvancedMarker
              key={pin.location.resource_name}
              position={{ lat: pin.lat, lng: pin.lng }}
              title={`${pin.location.canonical_name}${
                pin.location.reach ? ` — reach ${pin.location.reach.toLocaleString()}` : ''
              }`}
            >
              <Pin
                background={MAP_COLORS.teal}
                glyphColor={MAP_COLORS.tealDark}
                borderColor={MAP_COLORS.tealDark}
              />
            </AdvancedMarker>
          ))}
        </GoogleMap>
        {selected.length === 0 && (
          <div className="pointer-events-none absolute inset-x-3 bottom-3 rounded border border-border bg-card/90 px-3 py-2 text-xs text-muted-foreground shadow-sm">
            Search by name, or click the map to add a state/county-level target.
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Auto-fit the map viewport so every pin is visible with a bit of
 * padding. Runs whenever the pin set changes. Skipped for a single
 * pin (use the default zoom instead — `fitBounds` on one point
 * zooms way too far in).
 */
function FitToBounds({ pins }: { pins: GeocodedPin[] }) {
  const map = useMap();
  useEffect(() => {
    if (!map || pins.length === 0) return;
    const google = (window as unknown as { google?: typeof globalThis.google })
      .google;
    if (!google?.maps) return;

    // Single pin: fit to its bbox if we have one so the rectangle
    // overlay is fully visible. Without bounds, fall back to a
    // centered view at moderate zoom (8) so the pin isn't lost.
    if (pins.length === 1) {
      const p = pins[0];
      const b = new google.maps.LatLngBounds(
        { lat: p.highlightBounds.south, lng: p.highlightBounds.west },
        { lat: p.highlightBounds.north, lng: p.highlightBounds.east },
      );
      map.fitBounds(b, p.usedFallbackBounds ? 36 : 80);
      return;
    }

    // Multi-pin: aggregate bounds across all pins (prefer bboxes
    // when available — covers the whole region, not just centroid).
    const bounds = new google.maps.LatLngBounds();
    for (const p of pins) {
      bounds.extend({ lat: p.highlightBounds.north, lng: p.highlightBounds.east });
      bounds.extend({ lat: p.highlightBounds.south, lng: p.highlightBounds.west });
    }
    map.fitBounds(bounds, 60);
  }, [map, pins]);
  return null;
}


/**
 * Phase E.3 — region overlays for selected locations.
 *
 * For each pin, render a semi-transparent polygon/rectangle showing
 * the selected region's approximate coverage area.
 * Matches the "highlighted areas are locations you can include" UX
 * from Google Ads Keyword Planner.
 *
 * Uses imperative Google Maps overlays because @vis.gl/react-google-
 * maps doesn't ship declarative overlays for every shape yet. We
 * manage lifecycle inside the effect.
 *
 * NB: this is still a bbox approximation, not a true DMA polygon. When
 * Google Geocoding returns an obviously bad viewport for DMA/metro
 * targets, we draw a local fallback bbox around the geocoded point so
 * the operator sees a clear selected area instead of a tiny pin.
 */
function RegionOverlays({ pins }: { pins: GeocodedPin[] }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const google = (window as unknown as { google?: typeof globalThis.google })
      .google;
    if (!google?.maps) return;

    const overlays: Array<google.maps.Rectangle | google.maps.Polygon> = [];
    for (const pin of pins) {
      const baseOptions = {
        // Close to Google Ads' blue selected-region treatment while
        // remaining translucent enough for roads and city labels.
        strokeColor: MAP_COLORS.regionStroke,
        strokeOpacity: 0.95,
        strokeWeight: 2,
        fillColor: MAP_COLORS.regionFill,
        fillOpacity: pin.usedFallbackBounds ? 0.46 : 0.38,
        clickable: false,
        map,
      };
      if (pin.highlightPath) {
        overlays.push(
          new google.maps.Polygon({
            ...baseOptions,
            paths: pin.highlightPath,
          }),
        );
      } else {
        overlays.push(
          new google.maps.Rectangle({
            ...baseOptions,
            bounds: {
              north: pin.highlightBounds.north,
              south: pin.highlightBounds.south,
              east: pin.highlightBounds.east,
              west: pin.highlightBounds.west,
            },
          }),
        );
      }
    }
    return () => {
      for (const overlay of overlays) {
        overlay.setMap(null);
      }
    };
  }, [map, pins]);
  return null;
}


function selectHighlightBounds(
  loc: GeoTargetSuggestion,
  lat: number,
  lng: number,
  geocoderBounds?: GeoBounds,
): {
  bounds: GeoBounds;
  path?: GeoPoint[];
  source: GeocodedPin['boundsSource'];
} {
  if (geocoderBounds && isUsableGeocoderBounds(loc, lat, lng, geocoderBounds)) {
    return { bounds: geocoderBounds, source: 'geocoder' };
  }
  const fallback = fallbackHighlightForTarget(loc, lat, lng);
  return {
    bounds: fallback.bounds,
    path: fallback.path,
    source: 'local_fallback',
  };
}


function isUsableGeocoderBounds(
  loc: GeoTargetSuggestion,
  lat: number,
  lng: number,
  bounds: GeoBounds,
): boolean {
  const latSpan = Math.abs(bounds.north - bounds.south);
  const lngSpan = normalizedLngSpan(bounds.east, bounds.west);
  if (latSpan <= 0 || lngSpan <= 0) return false;

  const targetType = loc.target_type.toLowerCase();
  const centerLat = (bounds.north + bounds.south) / 2;
  const centerLng = midpointLng(bounds.east, bounds.west);
  const centerDistance = roughDistanceDegrees(lat, lng, centerLat, centerLng);

  if (targetType.includes('dma') || targetType.includes('metro')) {
    return (
      latSpan >= 1.15 &&
      lngSpan >= 1.15 &&
      latSpan <= 4.5 &&
      lngSpan <= 5.5 &&
      centerDistance <= 3.5
    );
  }
  if (targetType.includes('county')) {
    return latSpan <= 2.25 && lngSpan <= 2.75 && centerDistance <= 1.75;
  }
  if (targetType.includes('city')) {
    return latSpan <= 1.2 && lngSpan <= 1.4 && centerDistance <= 1;
  }
  if (targetType.includes('postal') || targetType.includes('zip')) {
    return latSpan <= 0.35 && lngSpan <= 0.45 && centerDistance <= 0.25;
  }

  // States, countries, and other broad Google Ads targets can have
  // legitimately large viewports, so only reject malformed bboxes.
  return latSpan <= 65 && lngSpan <= 120;
}


function fallbackBoundsForTarget(
  loc: GeoTargetSuggestion,
  lat: number,
  lng: number,
): GeoBounds {
  const targetType = loc.target_type.toLowerCase();

  let halfLat = 0.45;
  let halfLng = 0.6;
  if (targetType.includes('dma') || targetType.includes('metro')) {
    halfLat = 1.35;
    halfLng = 1.7;
  } else if (targetType.includes('county')) {
    halfLat = 0.55;
    halfLng = 0.75;
  } else if (targetType.includes('city')) {
    halfLat = 0.22;
    halfLng = 0.3;
  } else if (targetType.includes('postal') || targetType.includes('zip')) {
    halfLat = 0.06;
    halfLng = 0.08;
  } else if (targetType.includes('region') || targetType.includes('state')) {
    halfLat = 2.5;
    halfLng = 3.2;
  }

  return {
    north: clamp(lat + halfLat, -85, 85),
    south: clamp(lat - halfLat, -85, 85),
    east: normalizeLng(lng + halfLng),
    west: normalizeLng(lng - halfLng),
  };
}


function fallbackHighlightForTarget(
  loc: GeoTargetSuggestion,
  lat: number,
  lng: number,
): { bounds: GeoBounds; path?: GeoPoint[] } {
  const knownDmaPath = knownDmaPathForTarget(loc);
  if (knownDmaPath) {
    return {
      bounds: boundsFromPath(knownDmaPath),
      path: knownDmaPath,
    };
  }

  const bounds = fallbackBoundsForTarget(loc, lat, lng);
  return {
    bounds,
    path: fallbackPathForTarget(loc, lat, lng, bounds),
  };
}


function knownDmaPathForTarget(loc: GeoTargetSuggestion): GeoPoint[] | undefined {
  if (
    loc.id === '200588' ||
    loc.resource_name === 'geoTargetConstants/200588'
  ) {
    return ST_LOUIS_DMA_APPROX_PATH;
  }
  return undefined;
}


function fallbackPathForTarget(
  loc: GeoTargetSuggestion,
  lat: number,
  lng: number,
  bounds: GeoBounds,
): GeoPoint[] | undefined {
  const targetType = loc.target_type.toLowerCase();
  if (!targetType.includes('dma') && !targetType.includes('metro')) {
    return undefined;
  }

  const halfLat = Math.abs(bounds.north - bounds.south) / 2;
  const halfLng = normalizedLngSpan(bounds.east, bounds.west) / 2;
  return Array.from({ length: 24 }, (_, index) => {
    const angle = (index / 24) * Math.PI * 2;
    return {
      lat: clamp(lat + Math.sin(angle) * halfLat, -85, 85),
      lng: normalizeLng(lng + Math.cos(angle) * halfLng),
    };
  });
}


async function findGeoTargetForGeocodedResult({
  tenantId,
  result,
  selectedResourceNames,
}: {
  tenantId: string;
  result: google.maps.GeocoderResult;
  selectedResourceNames: Set<string>;
}): Promise<GeoTargetSuggestion | null> {
  const parsed = parseAddressComponents(result.address_components);
  const countryCode = parsed.countryShort || undefined;
  const queries = buildReverseGeocodeQueries(parsed, result.formatted_address);

  for (const query of queries) {
    const response = await searchGeoTargets({
      tenantId,
      query,
      countryCode,
      pageSize: 8,
    });
    const match = pickBestGeoTargetMatch(
      response.results,
      parsed,
      selectedResourceNames,
    );
    if (match) return match;
  }
  return null;
}


interface ParsedAddress {
  locality?: string;
  county?: string;
  state?: string;
  stateShort?: string;
  country?: string;
  countryShort?: string;
}


function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[],
): ParsedAddress {
  const parsed: ParsedAddress = {};
  for (const component of components) {
    if (component.types.includes('locality')) {
      parsed.locality = component.long_name;
    } else if (component.types.includes('administrative_area_level_2')) {
      parsed.county = component.long_name;
    } else if (component.types.includes('administrative_area_level_1')) {
      parsed.state = component.long_name;
      parsed.stateShort = component.short_name;
    } else if (component.types.includes('country')) {
      parsed.country = component.long_name;
      parsed.countryShort = component.short_name;
    }
  }
  return parsed;
}


function buildReverseGeocodeQueries(
  parsed: ParsedAddress,
  formattedAddress: string,
): string[] {
  const stateQuery = joinLocationParts(parsed.state, parsed.country);
  const countyQuery = joinLocationParts(parsed.county, parsed.state);
  const cityQuery = joinLocationParts(parsed.locality, parsed.state);
  return uniqueStrings([
    stateQuery,
    countyQuery,
    cityQuery,
    formattedAddress,
  ]);
}


function pickBestGeoTargetMatch(
  results: GeoTargetSuggestion[],
  parsed: ParsedAddress,
  selectedResourceNames: Set<string>,
): GeoTargetSuggestion | null {
  let best: { result: GeoTargetSuggestion; score: number } | null = null;
  for (const result of results) {
    if (selectedResourceNames.has(result.resource_name)) continue;
    if (result.status && result.status.toUpperCase() !== 'ENABLED') continue;
    if (
      parsed.countryShort &&
      result.country_code &&
      result.country_code !== parsed.countryShort
    ) {
      continue;
    }

    const score = scoreGeoTargetMatch(result, parsed);
    if (!best || score > best.score) {
      best = { result, score };
    }
  }
  return best && best.score >= 40 ? best.result : null;
}


function scoreGeoTargetMatch(
  result: GeoTargetSuggestion,
  parsed: ParsedAddress,
): number {
  const targetType = result.target_type.toLowerCase();
  const haystack = `${result.name} ${result.canonical_name}`.toLowerCase();
  let score = 0;

  // For arbitrary map clicks, broad state/county matches are safer
  // than accidentally adding a postal code from one rooftop result.
  if (targetType.includes('dma') || targetType.includes('metro')) score += 45;
  else if (targetType.includes('region') || targetType.includes('state')) score += 80;
  else if (targetType.includes('county')) score += 70;
  else if (targetType.includes('city')) score += 50;
  else if (targetType.includes('postal') || targetType.includes('zip')) score += 15;
  else if (targetType.includes('country')) score += 20;

  if (parsed.state && haystack.includes(parsed.state.toLowerCase())) score += 18;
  if (parsed.stateShort && haystack.includes(parsed.stateShort.toLowerCase())) {
    score += 8;
  }
  if (parsed.county && haystack.includes(parsed.county.toLowerCase())) score += 16;
  if (parsed.locality && haystack.includes(parsed.locality.toLowerCase())) {
    score += 12;
  }
  return score;
}


function joinLocationParts(...parts: Array<string | undefined>): string | null {
  const joined = parts.filter(Boolean).join(', ');
  return joined || null;
}


function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}


function normalizedLngSpan(east: number, west: number): number {
  const raw = Math.abs(east - west);
  return raw > 180 ? 360 - raw : raw;
}


function midpointLng(east: number, west: number): number {
  if (Math.abs(east - west) <= 180) {
    return (east + west) / 2;
  }
  return normalizeLng((east + west + 360) / 2);
}


function roughDistanceDegrees(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const lngDelta = normalizedLngSpan(aLng, bLng);
  return Math.hypot(aLat - bLat, lngDelta);
}


function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}


function normalizeLng(value: number): number {
  if (value > 180) return value - 360;
  if (value < -180) return value + 360;
  return value;
}


function boundsFromPath(path: GeoPoint[]): GeoBounds {
  const lats = path.map((p) => p.lat);
  const lngs = path.map((p) => p.lng);
  return {
    north: Math.max(...lats),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    west: Math.min(...lngs),
  };
}


function locationCacheKey(loc: GeoTargetSuggestion): string {
  return `${loc.resource_name}:regional-overlay-v3`;
}
